'use client';

import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import utc from 'dayjs/plugin/utc';
import { STANDARD_DATE_FORMAT } from 'src/constants/dates';
import { getColorVar } from 'src/utils/theme/colorVars';

dayjs.extend(isSameOrAfter);
dayjs.extend(utc);

export const getPositiveGreenColor = () => {
	return getColorVar('text-positive-green-button');
};
export const getNegativeRedColor = () => {
	return getColorVar('text-negative-red-button');
};

const getDateTicks = (
	firstDateMs: number,
	lastDateMs: number,
	interval: number,
	unit: dayjs.ManipulateType
) => {
	let lastDate = dayjs(lastDateMs);
	const firstDate = dayjs(firstDateMs);
	const ticks: number[] = [];
	while (lastDate.isSameOrAfter(firstDate)) {
		ticks.push(lastDate.valueOf());
		lastDate = lastDate.subtract(interval, unit);
	}
	return ticks.reverse();
};

type TimeBasedAxisInterval = '2h' | '1d' | '30d';

export const TICK_DURATION_MAP = {
	'1D': '2h' as const,
	'7D': '1d' as const,
	'1M': '30d' as const,
} as const;

export const getTimeBasedXAxisTicks = (
	firstDateMs: number,
	lastDateMs: number,
	interval: TimeBasedAxisInterval
): number[] => {
	if (interval === '2h') {
		// find the closest even hour that has past lastDateTs e.g. if lastDateTs represents 13:05, then we want 12:00
		let lastDateHour = dayjs(lastDateMs).hour();
		const isEvenHour = lastDateHour % 2 === 0;
		if (!isEvenHour) {
			lastDateHour -= 1;
		}

		const formattedLastDateMs = dayjs(lastDateMs)
			.hour(lastDateHour)
			.minute(0)
			.valueOf();

		return getDateTicks(firstDateMs, formattedLastDateMs, 2, 'hour');
	} else if (interval === '1d') {
		const ticks = getDateTicks(firstDateMs, lastDateMs, 1, 'day');
		return ticks;
	} else if (interval === '30d') {
		const ticks = getDateTicks(firstDateMs, lastDateMs, 6, 'day');
		return ticks;
	}

	return [];
};

export const getTimeBasedTickFormatter = (noYear = false) => {
	return (tick: number) => {
		const date = dayjs(tick);

		return date.format(`DD MMM ${noYear ? '' : 'YYYY'}`);
	};
};

export const getTooltipDate = (
	ts: number,
	{
		isRelativeToToday = false,
		dateWithTime = true,
	}: {
		isRelativeToToday?: boolean;
		dateWithTime?: boolean;
	} = {
		isRelativeToToday: false,
		dateWithTime: true,
	}
) => {
	if (isRelativeToToday) {
		const isToday = dayjs(ts).isSame(dayjs(), 'date');
		const isYesterday = dayjs(ts).isSame(dayjs().subtract(1, 'day'), 'date');
		const isTomorrow = dayjs(ts).isSame(dayjs().add(1, 'day'), 'date');

		if (isToday || isYesterday || isTomorrow) {
			return dayjs(ts).format(
				`h:mm A [${isToday ? 'Today' : isYesterday ? 'Yesterday' : 'Tomorrow'}]`
			);
		}
	}

	return dayjs(ts).format(
		`${dateWithTime ? ' h:mm A' : ''} ${STANDARD_DATE_FORMAT}`
	);
};

/**
 * The color of the area under the graph/line of graph works as such:
 *
 * The highest point (y-axis) of the graph is 0%, the lowest point is 100%.
 * We set the colors to change at X% of the height of the graph.
 * Since we want the colors to change at the x-axis (0 line), we find
 * the percentage of the graph from the top of the graph to the x-axis,
 * and set the colors to change at this offset.
 */
export const getAreaStops = (
	min: number,
	max: number,
	{ endOpacity = 1, startOpacity = 1 } = { endOpacity: 1, startOpacity: 1 },
	{
		topColor = getPositiveGreenColor(),
		bottomColor = getNegativeRedColor(),
	} = {
		topColor: getPositiveGreenColor(),
		bottomColor: getNegativeRedColor(),
	}
) => {
	if (min >= 0) {
		return (
			<>
				<stop offset="0%" stopColor={topColor} stopOpacity={startOpacity} />
				<stop offset="100%" stopColor={topColor} stopOpacity={endOpacity} />
			</>
		);
	}

	if (max <= 0) {
		return (
			<>
				<stop offset="0%" stopColor={bottomColor} stopOpacity={endOpacity} />
				<stop
					offset="100%"
					stopColor={bottomColor}
					stopOpacity={startOpacity}
				/>
			</>
		);
	}

	const zeroOffset = (max / (max - min)) * 100;

	return (
		<>
			<stop offset="0%" stopColor={topColor} stopOpacity={1} />
			<stop
				offset={`${zeroOffset}%`}
				stopColor={topColor}
				stopOpacity={endOpacity}
			/>
			<stop
				offset={`${zeroOffset}%`}
				stopColor={bottomColor}
				stopOpacity={endOpacity}
			/>
			<stop offset="100%" stopColor={bottomColor} stopOpacity={1} />
		</>
	);
};

export enum HistoricalTokenPriceDuration {
	ONE_DAY = '1',
	ONE_WEEK = '7',
	ONE_MONTH = '30',
	NINETY_DAYS = '90',
}

/**
 * Get custom x axis ticks based on duration
 *
 * ONE_DAY: every 2 hours
 * ONE_WEEK: every 1 day
 * ONE_MONTH: every 2 days
 */
export const getDurationBasedXAxisTicks = (
	firstDateMs: number,
	lastDateMs: number,
	duration: HistoricalTokenPriceDuration
): number[] => {
	if (duration === HistoricalTokenPriceDuration.ONE_DAY) {
		// find the closest even hour that has past lastDateTs e.g. if lastDateTs represents 13:05, then we want 12:00
		let lastDateHour = dayjs(lastDateMs).hour();
		const isEvenHour = lastDateHour % 2 === 0;
		if (!isEvenHour) {
			lastDateHour -= 1;
		}

		const formattedLastDateMs = dayjs(lastDateMs)
			.hour(lastDateHour)
			.minute(0)
			.valueOf();

		return getDateTicks(firstDateMs, formattedLastDateMs, 2, 'hour');
	} else if (duration === HistoricalTokenPriceDuration.ONE_WEEK) {
		// find start of day
		const formattedLastDateMs = dayjs(lastDateMs).hour(0).minute(0).valueOf();

		return getDateTicks(firstDateMs, formattedLastDateMs, 1, 'day');
	} else if (duration === HistoricalTokenPriceDuration.ONE_MONTH) {
		// find start of day
		const formattedLastDateMs = dayjs(lastDateMs).hour(0).minute(0).valueOf();

		return getDateTicks(firstDateMs, formattedLastDateMs, 2, 'day');
	}

	return [];
};

const CHAR_WIDTH = 8;
const Y_AXIS_PADDING = 12;

export const calculateYAxisWidth = (labels: string[]): number => {
	const longestLabel = labels.reduce(
		(a, b) => (a.length > b.length ? a : b),
		''
	);
	return longestLabel.length * CHAR_WIDTH + Y_AXIS_PADDING;
};
